import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { SEOHead, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then(r => setServices(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <main className="pt-16" data-testid="services-page">
      <SEOHead
        title="Industry Website Design — Websites for Every Business"
        description="Professional website design tailored to your industry. Plumbers, electricians, restaurants, real estate, beauty, builders, eCommerce and more. Melbourne & Australia-wide."
        path="/services"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])}
      />
      <Breadcrumbs items={[{ name: "Services", path: "/services" }]} />

      <section className="bg-[#0055FF] text-white border-b-4 border-black section-padding">
        <div className="container-main text-center">
          <p className="text-sm font-black uppercase tracking-widest text-[#FFD600] mb-4">Industry Solutions</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Websites Built for<br />Your Industry
          </h1>
          <p className="text-lg text-blue-200 font-medium max-w-2xl mx-auto">
            We design websites tailored to your specific industry. Every feature, every page, every word — built to help your type of business succeed online.
          </p>
        </div>
      </section>

      <section className="section-padding" data-testid="services-grid">
        <div className="container-main">
          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-black border-t-[#FFD600] rounded-full animate-spin mx-auto mb-4" />
              <p className="text-xl font-bold">Loading services...</p>
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-20" data-testid="services-empty">
              <p className="text-2xl font-black mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Websites for every industry</p>
              <p className="text-gray-600 font-medium">Tell us about your business and we'll build a website tailored to your industry.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((svc, i) => (
                <Link
                  key={svc.id || i}
                  to={`/services/${svc.slug}`}
                  className="group bg-white border-2 border-black rounded-2xl p-8 flex flex-col transition-transform hover:-translate-y-2 duration-200"
                  style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
                  data-testid={`service-card-${i}`}
                >
                  <h2 className="text-xl font-black mb-3 group-hover:text-[#0055FF] transition-colors" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {svc.title}
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed flex-1 mb-6">{svc.excerpt}</p>
                  <span className="font-bold text-[#0055FF] flex items-center gap-1 text-sm">
                    Learn More <ArrowRight size={14} />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section-padding bg-[#F4F4F5] border-t-4 border-black">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Don't See Your Industry?
          </h2>
          <p className="text-lg font-medium text-gray-600 mb-8 max-w-xl mx-auto">
            We build websites for every type of business. Tell us what you need and we'll create a custom solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="services-contact-cta">
              Get a Free Quote <ArrowRight size={16} />
            </Link>
            <Link to="/packages" className="btn-secondary inline-flex items-center gap-2" data-testid="services-packages-cta">
              View Packages <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section className="section-padding bg-[#FFD600] border-t-4 border-black">
        <div className="container-main text-center">
          <h2 className="text-3xl md:text-4xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Professional Websites from $499
          </h2>
          <p className="text-lg font-semibold mb-8">Mobile-friendly. SEO-ready. Live in 48 hours.</p>
          <Link to="/purchase" className="btn-secondary inline-flex items-center gap-2 text-lg" data-testid="services-buy-cta">
            Get Started Now <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
}
